import {
  Paper,
  Title,
  Text,
  Stack,
  Group,
  Badge,
  Box,
  Code,
  Progress,
  SimpleGrid,
  Tooltip,
} from '@mantine/core';
import { Bluetooth, Wifi, Radio, Car } from 'lucide-react';
import type { WirelessSignal } from '../types';

interface WirelessSignalsDisplayProps {
  signals: WirelessSignal[];
}

function getSignalIcon(type: string) {
  switch (type) {
    case 'ble':
      return <Bluetooth size={16} />;
    case 'wifi':
      return <Wifi size={16} />;
    case 'tpms':
      return <Car size={16} />;
    default:
      return <Radio size={16} />;
  }
}

function getSignalTypeColor(type: string): string {
  switch (type) {
    case 'wifi':
      return 'blue';
    case 'ble':
      return 'cyan';
    case 'bluetooth':
      return 'indigo';
    case 'tpms':
      return 'orange';
    default:
      return 'gray';
  }
}

function getSignalStrengthLabel(rssi: number): { label: string; color: string } {
  if (rssi >= -50) return { label: 'Excellent', color: 'green' };
  if (rssi >= -60) return { label: 'Good', color: 'teal' };
  if (rssi >= -70) return { label: 'Fair', color: 'yellow' };
  if (rssi >= -80) return { label: 'Weak', color: 'orange' };
  return { label: 'Very Weak', color: 'red' };
}

function SignalCard({ signal }: { signal: WirelessSignal }) {
  const strengthPercent = Math.max(0, Math.min(100, 100 + signal.rssi));
  const strengthInfo = getSignalStrengthLabel(signal.rssi);

  return (
    <Paper withBorder p="sm">
      <Stack gap="xs">
        <Group justify="space-between">
          <Group gap="xs">
            {getSignalIcon(signal.signal_type)}
            <Badge color={getSignalTypeColor(signal.signal_type)} size="sm">
              {signal.signal_type.toUpperCase()}
            </Badge>
          </Group>
          <Tooltip label={strengthInfo.label}>
            <Badge color={strengthInfo.color} variant="light" size="sm">
              {signal.rssi} dBm
            </Badge>
          </Tooltip>
        </Group>

        <Code style={{ fontSize: '0.75rem' }}>{signal.mac_address}</Code>

        <Box>
          <Progress
            value={strengthPercent}
            size="sm"
            color={strengthInfo.color}
          />
        </Box>

        {signal.metadata && Object.keys(signal.metadata).length > 0 && (
          <Box>
            {signal.metadata.device_name && (
              <Text size="xs" c="dimmed">
                Name: {signal.metadata.device_name}
              </Text>
            )}
            {signal.metadata.manufacturer && (
              <Text size="xs" c="dimmed">
                Mfr: {signal.metadata.manufacturer}
              </Text>
            )}
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

export function WirelessSignalsDisplay({ signals }: WirelessSignalsDisplayProps) {
  if (!signals || signals.length === 0) {
    return null;
  }

  const bleSignals = signals.filter((s) => s.signal_type === 'ble');
  const wifiSignals = signals.filter((s) => s.signal_type === 'wifi');
  const tpmsSignals = signals.filter((s) => s.signal_type === 'tpms');
  const otherSignals = signals.filter(
    (s) => !['ble', 'wifi', 'tpms'].includes(s.signal_type)
  );

  return (
    <Stack gap="md">
      <Group gap="xs">
        <Radio size={20} />
        <Title order={4}>Wireless Signals</Title>
        <Badge color="gray" variant="light">
          {signals.length} total
        </Badge>
      </Group>

      <Group gap="xs">
        {bleSignals.length > 0 && (
          <Badge color="cyan" leftSection={<Bluetooth size={12} />}>
            {bleSignals.length} BLE
          </Badge>
        )}
        {wifiSignals.length > 0 && (
          <Badge color="blue" leftSection={<Wifi size={12} />}>
            {wifiSignals.length} WiFi
          </Badge>
        )}
        {tpmsSignals.length > 0 && (
          <Badge color="orange" leftSection={<Car size={12} />}>
            {tpmsSignals.length} TPMS
          </Badge>
        )}
        {otherSignals.length > 0 && (
          <Badge color="gray" leftSection={<Radio size={12} />}>
            {otherSignals.length} Other
          </Badge>
        )}
      </Group>

      {bleSignals.length > 0 && (
        <Box>
          <Text size="sm" fw={500} mb="xs">
            BLE Signals ({bleSignals.length})
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
            {bleSignals.map((signal, idx) => (
              <SignalCard key={`ble-${idx}`} signal={signal} />
            ))}
          </SimpleGrid>
        </Box>
      )}

      {wifiSignals.length > 0 && (
        <Box>
          <Text size="sm" fw={500} mb="xs">
            WiFi Signals ({wifiSignals.length})
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
            {wifiSignals.map((signal, idx) => (
              <SignalCard key={`wifi-${idx}`} signal={signal} />
            ))}
          </SimpleGrid>
        </Box>
      )}

      {tpmsSignals.length > 0 && (
        <Box>
          <Text size="sm" fw={500} mb="xs">
            TPMS Signals ({tpmsSignals.length})
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
            {tpmsSignals.map((signal, idx) => (
              <SignalCard key={`tpms-${idx}`} signal={signal} />
            ))}
          </SimpleGrid>
        </Box>
      )}

      {otherSignals.length > 0 && (
        <Box>
          <Text size="sm" fw={500} mb="xs">
            Other Signals ({otherSignals.length})
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
            {otherSignals.map((signal, idx) => (
              <SignalCard key={`other-${idx}`} signal={signal} />
            ))}
          </SimpleGrid>
        </Box>
      )}
    </Stack>
  );
}
